import { action, makeAutoObservable, observable, reaction } from "mobx"
import Content from "store/content"
import { JsonSchema } from "store/json-schema"
import { makeContext } from "utils/provider"
import ContentBlock from "./block"
import Value from "./value"

class ContentEditorStore {

  contentBlocks: ContentBlock[] = []
  values: Value[] = []

  inactiveKeys = observable.set<string>([])
  isSaved = true
  baseKey: string = ""

  constructor() {
    makeAutoObservable(this, {
      inactiveKeys: false
    })
  }

  init() {
    return reaction(() => this.rawKeys, action(() => {
      this.isSaved = false
    }))
  }

  load(content: Content, schema: JsonSchema) {
    const blocks = Object.entries(schema.blocks)
    const emitters = new Set(schema.chains.map(item => item.to))
    const initialBlocks = blocks.filter(([ key, block ]) => !emitters.has(key))

    const sort = (a: ContentBlock, b: ContentBlock) => (a.block.position.top + a.block.position.left * 0.1) - (b.block.position.top + b.block.position.left * 0.1)

    this.contentBlocks = []
    for (let [ id, block ] of initialBlocks) {
      const contentBlock = new ContentBlock(id, block)
      this.contentBlocks.push(contentBlock)
      contentBlock.findChildren(id, schema, content)
    }

    this.contentBlocks.sort(sort)
    for (let block of this.contentBlocks) {
      block.sort(sort)
    }

    this.values = this.contentBlocks.map(block => new Value(block, content.data.values))
  }

  save() {
    const a: ValueStore = Object.fromEntries(this.values.map(val => val.save()).flat(1))
    this.isSaved = true
    return a
  }

  setBaseKey(baseKey: string) {
    this.baseKey = baseKey + (baseKey? ".": "")
  }

  get rawKeys() {
    const arr = []
    for (let val of this.values) {
      for (let item of val.keyValue) {
        if (!item[0]) continue
        arr.push(item)
      }
    }

    return arr.map(item => {
      if (item[0].startsWith("$_")) {
        const key = item[0].slice(2).split(".", 1)[0]
        const _key = item[0].slice(3+key.length)
        return [ this.baseKey.replace(/[^.]+\.section/, key+".section")+_key, item[1] ]
      }
      return [ this.baseKey + item[0], item[1] ]
    })
  }

  get rawCode() {
    return this.rawKeys.map(i => i.join("\n")).join("\n\n")
  }

  get consoleCode() {
    const arr = []
    arr.push(`
const findElement = (selector, text) => {
  for (const a of document.querySelectorAll(selector)){
    if (a.textContent.includes(text)) return a
  }
}

const pasteText = (el, text) => {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
  nativeInputValueSetter.call(el, text);
  el.dispatchEvent(new Event('input', { bubbles: true}));
}

const pasteTextareaText = (el, text) => {
  el.textContent = text
  el.dispatchEvent(new InputEvent("input", { bubbles: true }));
}

const addField = async (index, key, value) => {
  if (index > 1) {
    findElement('button', "Добавить ещё").click()
  }
  await new Promise(res => setTimeout(res, 10))
  const el = findElement('h3', 'Ключ '+index).parentElement
  pasteText(el.querySelector('input'), key)
  pasteTextareaText(el.querySelector('.contentEditable_KemijJkT'), value)
}
    `)
    for (let i = 0; i < this.rawKeys.length; i++) {
      const [ key, value ] = this.rawKeys[i]
      arr.push(`await addField(${i+1}, "${key}", "${value}")`)
    }

    return arr.join("\n")
  }

  findValue(callback: (value: Value) => boolean) {
    const checkValue = (value: Value): Value | null => {
      if (!value) return null
      if (callback(value)) return value
      for (let child of value.children) {
        if (Array.isArray(child)) {
          for (let ch of child) {
            const val = checkValue(ch)
            if (val) return val
          }
        } else {
          const val = checkValue(child)
          if (val) return val
        }
      }
      return null
    }
    for (let value of this.values) {
      const val = checkValue(value)
      if (val) return val
    }
    return null
  }
}

export default ContentEditorStore

const [ ContentEditorStoreProvider, useContentEditorStore ] = makeContext(() => new ContentEditorStore())
export { ContentEditorStoreProvider, useContentEditorStore, ContentEditorStore }

export type ValueStore = { [ key: string ]: string | ValueStore[] }